import { useState, useRef, useEffect } from 'react';
import { Menu, Moon, Sun, LogOut, ChevronDown, HelpCircle, Compass, Hand, KeyRound } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useUiStore } from '@/store/ui.store';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { roleLabel } from '@/lib/badges';
import { startTour } from '@/lib/tour';
import { MenuGuias } from '@/components/guia/MenuGuias';
import { CambiarClaveModal } from '@/components/common/CambiarClaveModal';
import { MenuVerComo } from './VerComo';
import { viendoComo, volverAMiCuenta } from '@/lib/ver-como';

export function Header() {
  const { user, logout } = useAuth();
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [guiasOpen, setGuiasOpen] = useState(false);
  const [claveOpen, setClaveOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) setHelpOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    // 21-oct, macOS 27: barra de herramientas de 52 (56 en el teléfono), fondo de la ventana y cápsulas de cristal.
    <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-surface-border bg-white px-3 dark:border-white/10 dark:bg-slate-950 lateral:h-[52px] lateral:px-4">
      <button
        onClick={toggleSidebar}
        className="mac-capsula w-11 lateral:w-9"
        aria-label="Alternar menú"
      >
        <Menu className="h-4 w-4" strokeWidth={1.75} />
      </button>

      <div className="flex items-center gap-2">
        {/* Menú de Ayuda: las guías paso a paso, el tour y el recordatorio de los "?". */}
        <div className="relative" ref={helpRef} data-tour="help">
          <button
            onClick={() => setHelpOpen((v) => !v)}
            className="mac-capsula w-11 lateral:w-9"
            aria-label="Ayuda"
          >
            <HelpCircle className="h-4 w-4" strokeWidth={1.75} />
          </button>
          {helpOpen && (
            <div className="mac-menu absolute right-0 z-50 mt-2 w-64 max-w-[calc(100vw-1.5rem)]">
              {/* "Muéstrame" a mano: la guía paso a paso, sin pasar por el asistente. */}
              <button
                onClick={() => {
                  setHelpOpen(false);
                  setGuiasOpen(true);
                }}
                className="mac-menu-fila font-medium"
              >
                <Hand className="h-4 w-4 text-acento-icono" />
                ¿Cómo hago…?
              </button>
              <button
                onClick={() => {
                  setHelpOpen(false);
                  startTour();
                }}
                className="mac-menu-fila"
              >
                <Compass className="h-4 w-4" />
                Ver el tour de bienvenida
              </button>
              <p className="px-[9px] py-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                Cada pantalla tiene un botón “?” junto al título que explica qué hacer ahí.
              </p>
            </div>
          )}
        </div>

        <button
          onClick={toggleTheme}
          className="mac-capsula w-11 lateral:w-9"
          aria-label="Cambiar tema"
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" strokeWidth={1.75} /> : <Moon className="h-4 w-4" strokeWidth={1.75} />}
        </button>

        <div className="relative" ref={ref}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="mac-capsula pl-1 pr-1 sm:pr-3 lateral:pl-1"
            aria-label="Mi cuenta"
          >
            <Avatar name={user?.name ?? user?.email} size="sm" />
            <div className="hidden text-left sm:block">
              <p className="max-w-[160px] truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                {user?.name ?? user?.email}
              </p>
            </div>
            <ChevronDown className="hidden h-3.5 w-3.5 text-slate-500 sm:block" />
          </button>

          {menuOpen && (
            <div className="mac-menu absolute right-0 z-50 mt-2 w-60 max-w-[calc(100vw-1.5rem)]">
              <div className="mb-1 border-b border-surface-border px-[9px] pb-2 pt-1.5 dark:border-white/10">
                <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                  {user?.name ?? '—'}
                </p>
                <p className="truncate text-xs text-slate-400">{user?.email}</p>
                {user?.role && (
                  <Badge variant="brand" className="mt-1.5">
                    {roleLabel[user.role] ?? user.role}
                  </Badge>
                )}
              </div>
              <MenuVerComo />
              {/* Mirando otra cuenta no se le cambia la clave (la API también lo frena). */}
              {!viendoComo() && (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setClaveOpen(true);
                  }}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <KeyRound className="h-4 w-4" />
                  Cambiar contraseña
                </button>
              )}
              <button
                // Mirando otra cuenta, «cerrar sesión» vuelve a la de la administración.
                onClick={() => (viendoComo() ? volverAMiCuenta() : void logout())}
                className="mac-menu-fila"
              >
                <LogOut className="h-4 w-4" />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
      <MenuGuias open={guiasOpen} onClose={() => setGuiasOpen(false)} />
      <CambiarClaveModal open={claveOpen} onClose={() => setClaveOpen(false)} />
    </header>
  );
}
